import React, { useState, useEffect } from 'react';
import { usuariosAPI, suscripcionesAPI, pagosUsuariosAPI } from '../services/api';
import UsuarioForm from './UsuarioForm';
import './Usuarios.css';

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [suscripciones, setSuscripciones] = useState({});
  const [pagosPendientes, setPagosPendientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filtroRol, setFiltroRol] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const [usrResponse, susResponse] = await Promise.all([
        usuariosAPI.getAll(null),
        suscripcionesAPI.getAll()
      ]);
      
      const usuariosData = usrResponse.data || [];
      setUsuarios(usuariosData);
      
      // Cargar suscripciones activas
      const suscripcionesData = susResponse.data?.data || [];
      const suscripcionesMap = {};
      suscripcionesData.forEach(sus => {
        if (sus.estado === 'activa') {
          suscripcionesMap[sus.usuario_id] = sus;
        }
      });
      setSuscripciones(suscripcionesMap);
      
      // Cargar pagos pendientes por usuario
      const pagosMap = {};
      for (const usuario of usuariosData) {
        if (usuario.rol !== 'admin') {
          try {
            const pagosRes = await pagosUsuariosAPI.getByUsuario(usuario.id, 'pendiente');
            const pendientes = pagosRes.data?.data || [];
            if (pendientes.length > 0) {
              pagosMap[usuario.id] = pendientes;
            }
          } catch (e) {
            // Ignorar errores individuales
          }
        }
      }
      setPagosPendientes(pagosMap);
      
      setError(null);
    } catch (err) {
      console.error('Error loading usuarios:', err);
      setError('Error al cargar los usuarios. Verifica que tengas permisos de administrador.');
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUsuario(null);
    setShowForm(true);
  };

  const handleEdit = (usuario) => {
    setEditingUsuario(usuario);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres desactivar este usuario?')) {
      return;
    }
    try {
      await usuariosAPI.delete(id);
      setSuccess('Usuario desactivado correctamente');
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al desactivar el usuario');
      console.error(err);
    }
  };

  const handleActivate = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres reactivar este usuario?')) {
      return;
    }
    try {
      const usuarioActual = usuarios.find(u => u.id === id);
      if (usuarioActual) {
        await usuariosAPI.update(id, { ...usuarioActual, activo: 1 });
        setSuccess('Usuario reactivado correctamente');
        loadUsuarios();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Error al reactivar el usuario');
      console.error(err);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingUsuario(null);
  };

  const handleFormSubmit = async (data) => {
    try {
      if (editingUsuario) {
        await usuariosAPI.update(editingUsuario.id, data);
        setSuccess('Usuario actualizado correctamente');
      } else {
        await usuariosAPI.create(data);
        setSuccess('Usuario creado correctamente');
      }
      setShowForm(false);
      setEditingUsuario(null);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el usuario');
      console.error(err);
    }
  };

  const handleToggleFacturacion = async (usuario) => {
    try {
      const nuevoValor = !usuario.facturacion_habilitada;
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        facturacion_habilitada: nuevoValor
      });
      setSuccess(`Facturación ${nuevoValor ? 'habilitada' : 'deshabilitada'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar el permiso de facturación');
      console.error(err);
    }
  };

  const handleToggleOportunidades = async (usuario) => {
    try {
      const nuevoValor = !usuario.oportunidades_habilitadas;
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        oportunidades_habilitadas: nuevoValor
      });
      setSuccess(`Oportunidades ${nuevoValor ? 'habilitadas' : 'deshabilitadas'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar el permiso de oportunidades');
      console.error(err);
    }
  };

  const handleToggleTareas = async (usuario) => {
    try {
      const nuevoValor = !usuario.tareas_habilitadas;
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        tareas_habilitadas: nuevoValor
      });
      setSuccess(`Tareas ${nuevoValor ? 'habilitadas' : 'deshabilitadas'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar el permiso de tareas');
      console.error(err);
    }
  };

  const handleToggleDisponibilidad = async (usuario) => {
    try {
      const nuevoValor = !usuario.disponibilidad_habilitada;
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        disponibilidad_habilitada: nuevoValor
      });
      setSuccess(`Disponibilidad ${nuevoValor ? 'habilitada' : 'deshabilitada'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar el permiso de disponibilidad');
      console.error(err);
    }
  };

  const handleToggleInteracciones = async (usuario) => {
    try {
      const nuevoValor = !usuario.interacciones_habilitadas;
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        interacciones_habilitadas: nuevoValor
      });
      setSuccess(`Interacciones ${nuevoValor ? 'habilitadas' : 'deshabilitadas'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar el permiso de interacciones');
      console.error(err);
    }
  };

  const handleToggleArchivos = async (usuario) => {
    try {
      const nuevoValor = !(usuario.archivos_habilitados === true || usuario.archivos_habilitados === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        archivos_habilitados: nuevoValor
      });
      setSuccess(`Archivos ${nuevoValor ? 'habilitados' : 'deshabilitados'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar el permiso de archivos');
      console.error(err);
    }
  };

  const handleToggleIntegraciones = async (usuario) => {
    try {
      const nuevoValor = !(usuario.integraciones_habilitadas === true || usuario.integraciones_habilitadas === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        integraciones_habilitadas: nuevoValor
      });
      setSuccess(`Integraciones ${nuevoValor ? 'habilitadas' : 'deshabilitadas'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar el permiso de integraciones');
      console.error(err);
    }
  };

  const handleToggleEinvoice = async (usuario) => {
    try {
      const nuevoValor = !(usuario.einvoice_habilitado === true || usuario.einvoice_habilitado === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        einvoice_habilitado: nuevoValor
      });
      setSuccess(`Facturación electrónica UE (e-invoice) ${nuevoValor ? 'habilitada' : 'deshabilitada'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar e-invoice');
      console.error(err);
    }
  };

  const handleToggleWhatsapp = async (usuario) => {
    try {
      const nuevoValor = !(usuario.whatsapp_habilitado === true || usuario.whatsapp_habilitado === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        whatsapp_habilitado: nuevoValor
      });
      setSuccess(`WhatsApp ${nuevoValor ? 'habilitado' : 'deshabilitado'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar WhatsApp');
      console.error(err);
    }
  };

  const handleTogglePortalCliente = async (usuario) => {
    try {
      const nuevoValor = !(usuario.portal_cliente_habilitado === true || usuario.portal_cliente_habilitado === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        portal_cliente_habilitado: nuevoValor
      });
      setSuccess(`Portal del cliente ${nuevoValor ? 'habilitado' : 'deshabilitado'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar portal del cliente');
      console.error(err);
    }
  };

  const handleToggleDashboardKpis = async (usuario) => {
    try {
      const nuevoValor = !(usuario.dashboard_kpis_habilitado === true || usuario.dashboard_kpis_habilitado === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        dashboard_kpis_habilitado: nuevoValor
      });
      setSuccess(`Dashboard KPIs ${nuevoValor ? 'habilitado' : 'deshabilitado'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar Dashboard KPIs');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleToggleCalendarioVisual = async (usuario) => {
    try {
      const nuevoValor = !(usuario.calendario_visual_habilitado === true || usuario.calendario_visual_habilitado === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        calendario_visual_habilitado: nuevoValor
      });
      setSuccess(`Calendario visual ${nuevoValor ? 'habilitado' : 'deshabilitado'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar calendario visual');
      console.error(err);
    }
  };

  const handleToggleNotificaciones = async (usuario) => {
    try {
      const nuevoValor = !(usuario.notificaciones_habilitadas === true || usuario.notificaciones_habilitadas === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        notificaciones_habilitadas: nuevoValor
      });
      setSuccess(`Notificaciones ${nuevoValor ? 'habilitadas' : 'deshabilitadas'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar notificaciones');
      console.error(err);
    }
  };

  const handleToggleCitasGrupales = async (usuario) => {
    try {
      const nuevoValor = !(usuario.citas_grupales_habilitadas === true || usuario.citas_grupales_habilitadas === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        citas_grupales_habilitadas: nuevoValor
      });
      setSuccess(`Citas grupales ${nuevoValor ? 'habilitadas' : 'deshabilitadas'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar citas grupales');
      console.error(err);
    }
  };

  const handleToggleVideollamadas = async (usuario) => {
    try {
      const nuevoValor = !(usuario.videollamadas_habilitadas === true || usuario.videollamadas_habilitadas === 1);
      await usuariosAPI.update(usuario.id, {
        ...usuario,
        videollamadas_habilitadas: nuevoValor
      });
      setSuccess(`Videollamadas ${nuevoValor ? 'habilitadas' : 'deshabilitadas'} para ${usuario.nombre}`);
      loadUsuarios();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al actualizar videollamadas');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="loading">Cargando usuarios...</div>;
  }

  return (
    <div className="usuarios">
      <div className="page-header">
        <h2>Gestión de Usuarios</h2>
        <button className="btn btn-primary" onClick={handleCreate}>
          Nuevo Usuario
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="usuarios-filtros" style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#555' }}>Rol:</label>
          <select
            value={filtroRol}
            onChange={(e) => setFiltroRol(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
          >
            <option value="todos">Todos</option>
            <option value="admin">Administrador</option>
            <option value="coach">Usuario</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#555' }}>Tipo:</label>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
          >
            <option value="todos">Todos</option>
            <option value="coach">Coach</option>
            <option value="comercial">Comercial</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#555' }}>Estado:</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
          >
            <option value="todos">Todos</option>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </select>
        </div>
        <span style={{ fontSize: '12px', color: '#999' }}>
          {usuarios.filter(u => {
            if (filtroRol !== 'todos' && u.rol !== filtroRol) return false;
            if (filtroTipo !== 'todos' && (u.tipo_usuario || 'coach') !== filtroTipo) return false;
            if (filtroEstado === 'activo' && !u.activo) return false;
            if (filtroEstado === 'inactivo' && u.activo) return false;
            return true;
          }).length} usuario(s)
        </span>
      </div>

      <div className="usuarios-list">
        {usuarios.filter(usuario => {
          if (filtroRol !== 'todos' && usuario.rol !== filtroRol) return false;
          if (filtroTipo !== 'todos' && (usuario.tipo_usuario || 'coach') !== filtroTipo) return false;
          if (filtroEstado === 'activo' && !usuario.activo) return false;
          if (filtroEstado === 'inactivo' && usuario.activo) return false;
          return true;
        }).map(usuario => (
          <div key={usuario.id} className={`usuario-card ${!usuario.activo ? 'inactiva' : ''}`}>
            <div className="usuario-header">
              <div>
                <h3>{usuario.nombre} {usuario.apellidos}</h3>
                <p className="usuario-info">
                  <strong>Username:</strong> {usuario.username} | 
                  <strong> Rol:</strong> {usuario.rol === 'admin' ? '👑 Administrador' : '👤 Coach'} |
                  {usuario.rol !== 'admin' && (<><strong> Tipo:</strong> {usuario.tipo_usuario === 'comercial' ? '💼 Comercial' : '🎯 Coach'} |</>)}
                  <strong> Estado:</strong> {usuario.activo ? '✅ Activo' : '❌ Inactivo'}
                </p>
                {usuario.email && (
                  <p className="usuario-email">
                    <strong>Email:</strong> {usuario.email}
                  </p>
                )}
                <p className="usuario-fecha">
                  Creado: {new Date(usuario.creado_en).toLocaleDateString('es-ES')}
                </p>

                {/* Correo: personalizado o genérico (solo coaches) */}
                {usuario.rol === 'coach' && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#495057' }}>
                    <strong>Correo:</strong> {(usuario.correo_personalizado === true || usuario.correo_personalizado === 1) && usuario.smtp_host ? 'Personalizado' : 'Genérico (config.php)'}
                  </div>
                )}

                {/* Datos fiscales (solo lectura para usar en facturas como admin) */}
                <div className="datos-fiscales-readonly">
                  <strong style={{ display: 'block', marginBottom: '6px', color: '#495057' }}>Datos fiscales</strong>
                  <div className="datos-fiscales-grid">
                    {(usuario.nombre_empresa || usuario.nif_cif || usuario.direccion || usuario.codigo_postal || usuario.ciudad || usuario.provincia || usuario.telefono || usuario.email) ? (
                      <>
                        {usuario.nombre_empresa && <p><strong>Empresa:</strong> {usuario.nombre_empresa}</p>}
                        {usuario.nif_cif && <p><strong>NIF/CIF:</strong> {usuario.nif_cif}</p>}
                        {usuario.direccion && <p><strong>Dirección:</strong> {usuario.direccion}</p>}
                        {(usuario.codigo_postal || usuario.ciudad) && (
                          <p><strong>CP / Localidad:</strong> {[usuario.codigo_postal, usuario.ciudad].filter(Boolean).join(' - ')}{usuario.provincia ? `, ${usuario.provincia}` : ''}</p>
                        )}
                        {usuario.telefono && <p><strong>Teléfono:</strong> {usuario.telefono}</p>}
                        {usuario.email && <p><strong>Email:</strong> {usuario.email}</p>}
                      </>
                    ) : (
                      <p style={{ color: '#999', fontStyle: 'italic' }}>Sin datos fiscales. Edita el usuario para añadirlos (se usarán en las facturas).</p>
                    )}
                  </div>
                </div>
                
                {/* Información de Suscripción */}
                {usuario.rol !== 'admin' && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                    <strong style={{ display: 'block', marginBottom: '8px' }}>Estado de Suscripción:</strong>
                    {suscripciones[usuario.id] ? (
                      <div>
                        <p style={{ margin: '5px 0' }}>
                          <strong>Plan:</strong> {suscripciones[usuario.id].plan_nombre}
                        </p>
                        <p style={{ margin: '5px 0' }}>
                          <strong>Vence:</strong> {new Date(suscripciones[usuario.id].fecha_vencimiento).toLocaleDateString('es-ES')}
                        </p>
                        <p style={{ margin: '5px 0' }}>
                          {suscripciones[usuario.id].estado === 'activa' ? (
                            <span className="badge badge-success">✅ Activa</span>
                          ) : (
                            <span className="badge badge-danger">❌ Inactiva</span>
                          )}
                          {usuario.bloqueado_por_pago && (
                            <span className="badge badge-danger" style={{ marginLeft: '5px' }}>
                              🔒 Bloqueado
                            </span>
                          )}
                        </p>
                        {pagosPendientes[usuario.id] && pagosPendientes[usuario.id].length > 0 && (
                          <p style={{ margin: '5px 0', color: '#dc3545' }}>
                            <strong>⚠️ {pagosPendientes[usuario.id].length} pago(s) pendiente(s)</strong>
                          </p>
                        )}
                      </div>
                    ) : (
                      <p style={{ color: '#dc3545', margin: 0 }}>❌ Sin suscripción activa</p>
                    )}
                  </div>
                )}
                
                {usuario.rol !== 'admin' && (
                <div className="usuario-checkbox" style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.facturacion_habilitada || false}
                      onChange={() => handleToggleFacturacion(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Facturación en el menú</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.oportunidades_habilitadas || false}
                      onChange={() => handleToggleOportunidades(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Oportunidades en el menú</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.tareas_habilitadas || false}
                      onChange={() => handleToggleTareas(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Tareas en el menú</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.interacciones_habilitadas !== undefined ? usuario.interacciones_habilitadas : true}
                      onChange={() => handleToggleInteracciones(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Interacciones en el menú</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.archivos_habilitados !== undefined ? (usuario.archivos_habilitados === true || usuario.archivos_habilitados === 1) : true}
                      onChange={() => handleToggleArchivos(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Archivos de cliente</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.disponibilidad_habilitada === true || usuario.disponibilidad_habilitada === 1}
                      onChange={() => handleToggleDisponibilidad(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Disponibilidad / Reservas en el menú</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.integraciones_habilitadas === true || usuario.integraciones_habilitadas === 1}
                      onChange={() => handleToggleIntegraciones(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Integraciones en el menú</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.einvoice_habilitado === true || usuario.einvoice_habilitado === 1}
                      onChange={() => handleToggleEinvoice(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Facturación electrónica UE (e-invoice)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.whatsapp_habilitado === true || usuario.whatsapp_habilitado === 1}
                      onChange={() => handleToggleWhatsapp(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar WhatsApp (botones de envío por WhatsApp)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.portal_cliente_habilitado === true || usuario.portal_cliente_habilitado === 1}
                      onChange={() => handleTogglePortalCliente(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Portal del Cliente (los clientes pueden ver citas, documentos, facturas)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.calendario_visual_habilitado === true || usuario.calendario_visual_habilitado === 1}
                      onChange={() => handleToggleCalendarioVisual(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Calendario Visual Pro (FullCalendar con drag &amp; drop)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.dashboard_kpis_habilitado === true || usuario.dashboard_kpis_habilitado === 1}
                      onChange={() => handleToggleDashboardKpis(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Dashboard con KPIs (tendencias, gráficas, pipeline)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.notificaciones_habilitadas === true || usuario.notificaciones_habilitadas === 1}
                      onChange={() => handleToggleNotificaciones(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Notificaciones (campana con alertas de reservas, citas, pagos)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.citas_grupales_habilitadas === true || usuario.citas_grupales_habilitadas === 1}
                      onChange={() => handleToggleCitasGrupales(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Citas Grupales (múltiples clientes por cita)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={usuario.videollamadas_habilitadas === true || usuario.videollamadas_habilitadas === 1}
                      onChange={() => handleToggleVideollamadas(usuario)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Habilitar Videollamadas (Jitsi Meet integrado en citas Online)</span>
                  </label>
                </div>
                )}
              </div>
              <div className="usuario-actions">
                <button className="btn btn-edit btn-sm" onClick={() => handleEdit(usuario)}>
                  Editar
                </button>
                {usuario.activo ? (
                  <button 
                    className="btn btn-warning btn-sm" 
                    onClick={() => handleDelete(usuario.id)}
                  >
                    Desactivar
                  </button>
                ) : (
                  <button 
                    className="btn btn-success btn-sm" 
                    onClick={() => handleActivate(usuario.id)}
                  >
                    Reactivar
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {usuarios.length === 0 ? (
        <div className="empty-state">
          <p>No hay usuarios registrados. Crea el primero haciendo clic en "Nuevo Usuario".</p>
        </div>
      ) : usuarios.filter(u => {
        if (filtroRol !== 'todos' && u.rol !== filtroRol) return false;
        if (filtroTipo !== 'todos' && (u.tipo_usuario || 'coach') !== filtroTipo) return false;
        if (filtroEstado === 'activo' && !u.activo) return false;
        if (filtroEstado === 'inactivo' && u.activo) return false;
        return true;
      }).length === 0 && (
        <div className="empty-state">
          <p>No hay usuarios que coincidan con los filtros seleccionados.</p>
        </div>
      )}

      {showForm && (
        <UsuarioForm
          usuario={editingUsuario}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
}

export default Usuarios;

